/*
 * Zendesk integration for the Comms tab. Every call is a POST to the same
 * function, differing only by `action` (+ a couple of extra fields). Points at
 * the Netlify demo function today; flip USE_MOCK on to serve the mock data
 * below instead, and change ZENDESK_FN once our own backend serves these.
 */

// SWAP POINT: base endpoint + mock toggle.
export const ZENDESK_FN = 'https://command-center-redesign.netlify.app/.netlify/functions/zendesk';
// Typed as boolean so flipping it doesn't trip no-unnecessary-condition.
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
export const USE_MOCK: boolean = false;

/*
 * Write actions are enabled one at a time, as each one's payload is confirmed
 * against the function — so nobody mutates live Zendesk data through a
 * half-defined payload. `status` is live; create/reply stay blocked until their
 * payloads are settled. guardWrite() short-circuits the ones still disabled.
 */
export type ZendeskWrite = 'create' | 'reply' | 'status';

export const WRITES_ENABLED: Record<ZendeskWrite, boolean> = {
  create: true,
  reply: true,
  status: true
};

export const DEMO_WRITE_MESSAGE = 'This action is not available during the demo.';

// Returns the block message when that write is disabled, or undefined when allowed.
export const guardWrite = (write: ZendeskWrite): string | undefined =>
  WRITES_ENABLED[write] ? undefined : DEMO_WRITE_MESSAGE;

export type ZendeskAction =
  | 'agents'
  | 'comments'
  | 'create'
  | 'fields'
  | 'list'
  | 'reply'
  | 'status';

/** Statuses the `status` action accepts (Zendesk custom field values). */
export const ZENDESK_STATUSES = [
  'open',
  'monitoring',
  'investigating',
  'onhold',
  'resolved'
] as const;

export type ZendeskStatus = (typeof ZENDESK_STATUSES)[number];

/** Priorities the `create` action accepts. */
export const ZENDESK_PRIORITIES = ['low', 'normal', 'high'] as const;

export type ZendeskPriority = (typeof ZENDESK_PRIORITIES)[number];

// --- Requests -------------------------------------------------------------

export interface ZendeskListRequest {
  action: 'list';
  eid: string;
  cid: string;
}

export interface ZendeskAgentsRequest {
  action: 'agents';
}

export interface ZendeskFieldsRequest {
  action: 'fields';
}

export interface ZendeskCommentsRequest {
  action: 'comments';
  ticketId: string;
}

export interface ZendeskStatusRequest {
  action: 'status';
  ticketId: string;
  status: ZendeskStatus;
}

export interface ZendeskReplyRequest {
  action: 'reply';
  ticketId: string;
  /** Message text; posted as a comment on the ticket. */
  body: string;
}

/** Image sent inline with a new ticket, as a base64 `data:` URL. */
export interface ZendeskAttachmentPayload {
  name: string;
  dataUrl: string;
}

export interface ZendeskCreateRequest {
  action: 'create';
  eid: string;
  cid: string;
  eventName: string;
  eventType: string;
  subject: string;
  requesterEmail: string;
  requesterName: string;
  /** Assignee key from the `agents` action ('group:…' or 'user:…'). */
  assignee: string;
  /** Issue-type value from the `fields` action. */
  issueType: string;
  priority: ZendeskPriority;
  serviceImpact: boolean;
  /** Name of the Command Center user filing the ticket. */
  filedBy: string;
  /** Description fields joined into the single Zendesk note. */
  body: string;
  attachments: ZendeskAttachmentPayload[];
}

export type ZendeskRequest =
  | ZendeskAgentsRequest
  | ZendeskCommentsRequest
  | ZendeskCreateRequest
  | ZendeskFieldsRequest
  | ZendeskListRequest
  | ZendeskReplyRequest
  | ZendeskStatusRequest;

/*
 * Generic result for mutating actions (status change, etc.). Success carries
 * `subdomain` + whatever the action echoes back (`status` for a status change);
 * failure carries `reason` ('upstream', …) and a human-readable `detail`.
 */
export interface ZendeskActionResponse {
  subdomain?: string;
  ok: boolean;
  /** Status the ticket ended up on — echoed by the `status` action on success. */
  status?: ZendeskStatus;
  /** New ticket id / number — returned by the `create` action on success. */
  id?: string;
  num?: string;
  reason?: string;
  detail?: string;
}

// --- Responses ------------------------------------------------------------

interface ZendeskBase {
  subdomain: string;
  ok: boolean;
}

export interface ZendeskTicket {
  id: string;
  num: string;
  title: string;
  requester: string;
  channel: string;
  status: string;
  priority: string;
  createdAt: string;
  updatedAt: string;
  /** Whether the ticket is tied to the current event. */
  thisEvent: boolean;
}

export interface ZendeskListResponse extends ZendeskBase {
  orgId: number;
  solvedFallback: boolean;
  tickets: ZendeskTicket[];
}

/** Assignee option (either a group or an individual user). */
export interface ZendeskAssignee {
  key: string;
  label: string;
}

export interface ZendeskAgentsResponse extends ZendeskBase {
  assignees: ZendeskAssignee[];
}

/** Issue-type option for a new ticket. */
export interface ZendeskIssueType {
  value: string;
  label: string;
}

export interface ZendeskFieldsResponse extends ZendeskBase {
  issueTypes: ZendeskIssueType[];
}

/** File attached to a comment. `url` serves both the link and the thumbnail. */
export interface ZendeskAttachment {
  id: string;
  name: string;
  url: string;
}

export interface ZendeskComment {
  id: string;
  who: string;
  role: string;
  /** Whether this comment is from us (LiveControl). */
  me: boolean;
  /** Whether it's an internal note (vs a public reply). */
  note: boolean;
  text: string;
  time: string;
  attachments: ZendeskAttachment[];
}

export interface ZendeskThreadResponse extends ZendeskBase {
  thread: ZendeskComment[];
}

// --- Mock data ------------------------------------------------------------

const MOCK_SUBDOMAIN = 'mylivecontrol';

export const mockList = (): ZendeskListResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true,
  orgId: 11533203670935,
  solvedFallback: false,
  tickets: [
    {
      id: '57877',
      num: '57877',
      title: 'Re: LiveControl Reels Edits',
      requester: 'Jeremy Gruber',
      channel: 'email',
      status: 'open',
      priority: 'normal',
      createdAt: '2026-08-03T18:26:59Z',
      updatedAt: '2026-08-03T18:26:59Z',
      thisEvent: true
    },
    {
      id: '57872',
      num: '57872',
      title: 'Re: Flying Lotus - Blue Note, Los Angeles',
      requester: 'Ben Olivas',
      channel: 'email',
      status: 'open',
      priority: 'high',
      createdAt: '2026-08-03T17:43:45Z',
      updatedAt: '2026-08-03T18:24:21Z',
      thisEvent: false
    },
    {
      id: '55466',
      num: '55466',
      title: 'LiveControl Priority Shows - Blue Note LA',
      requester: 'Jeremy Gruber',
      channel: 'email',
      status: 'monitoring',
      priority: 'normal',
      createdAt: '2026-07-10T17:44:36Z',
      updatedAt: '2026-07-21T22:17:33Z',
      thisEvent: false
    }
  ]
});

export const mockAgents = (): ZendeskAgentsResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true,
  assignees: [
    { key: 'group:10842493640087', label: 'Admin (group)' },
    { key: 'group:10997481221783', label: 'Event Support (group)' },
    { key: 'group:26008472310807', label: 'Production Success (group)' },
    { key: 'user:12101088450967', label: 'Alexandra Beckemeyer Groome' },
    { key: 'user:18446991022487', label: 'Antonio Diaz' },
    { key: 'user:12101369247255', label: 'Dustin Hulton' },
    { key: 'user:13587964538391', label: 'Jacob Braunstein' },
    { key: 'user:12958457481495', label: 'Ryan Mozek' }
  ]
});

export const mockFields = (): ZendeskFieldsResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true,
  issueTypes: [
    { value: 'technical_production_review', label: 'Technical::Production Review' },
    { value: 'technical_hardware_swap_reinstall', label: 'Technical::Hardware Swap / Reinstall' },
    {
      value: 'technical_client_monitoring_external_display',
      label: 'Technical::Client Monitoring - External Display'
    },
    { value: 'technical_static_shot_adjustment', label: 'Technical::Static Shot adjustment' }
  ]
});

export const mockStatus = (status: ZendeskStatus): ZendeskActionResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true,
  status
});

export const mockReply = (): ZendeskActionResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true
});

export const mockCreate = (): ZendeskActionResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true,
  id: '58786',
  num: '58786'
});

export const mockThread = (ticketId: string): ZendeskThreadResponse => ({
  subdomain: MOCK_SUBDOMAIN,
  ok: true,
  thread: [
    {
      id: `${ticketId}-1`,
      who: 'Steven Bensusan',
      role: 'Client',
      me: false,
      note: false,
      text: 'How can we stop these emails that everyone gets that asks how you did? We don’t want them.',
      time: '2026-02-15T15:23:41Z',
      attachments: []
    },
    {
      id: `${ticketId}-2`,
      who: 'Alexandra Beckemeyer Groome',
      role: 'LiveControl',
      me: true,
      note: false,
      text: 'Hi Steven, I have turned off all event-related notifications for you specifically.',
      time: '2026-02-17T15:09:20Z',
      attachments: []
    },
    {
      id: `${ticketId}-3`,
      who: 'Alexandra Beckemeyer Groome',
      role: 'LiveControl',
      me: true,
      note: true,
      text: 'flagged to jesus',
      time: '2026-03-11T18:20:57Z',
      attachments: [
        {
          id: '42702816189719',
          name: 'test-image.png',
          url: 'https://mylivecontrol.zendesk.com/attachments/token/9F1jk1Cav9VGeqC0DzgEngtoB/?name=test-image.png'
        }
      ]
    }
  ]
});

// --- Call -----------------------------------------------------------------

/*
 * Single entry point for every Zendesk POST. Returns the mock when USE_MOCK is
 * on; otherwise hits the real function. `mock` builds the fallback response so
 * each caller stays fully typed.
 */
export const zendeskCall = async <T>(payload: ZendeskRequest, mock: () => T): Promise<T> => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- USE_MOCK is a manual toggle
  if (USE_MOCK) {
    return mock();
  }

  const response = await fetch(ZENDESK_FN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  /*
   * Failures can arrive as a JSON body ({ ok: false, reason, detail }) on either
   * a 2xx or an error status, so read the body first and reuse its `detail`.
   */
  const body = <ZendeskActionResponse | undefined>await response.json().catch(() => undefined);

  if (!response.ok) {
    throw new Error(body?.detail ?? body?.reason ?? `HTTP ${response.status}`);
  }

  if (body === undefined) {
    throw new Error('Zendesk returned an unreadable response');
  }

  return <T>(<unknown>body);
};
